import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function VoiceSetup() {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [voiceId, setVoiceId] = useState(null);
  const [timer, setTimer] = useState(60);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTimer(60);

      // Start countdown timer
      timerIntervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-stop after 60 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, 60000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  };

  const uploadAndClone = async () => {
    if (!recordedBlob) {
      setError('Please record your voice first');
      return;
    }

    if (!user) {
      setError('Please sign in to continue');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Upload audio file
      const formData = new FormData();
      formData.append('file', recordedBlob, 'voice_sample.webm');

      const uploadResponse = await axios.post(
        `${API_URL}/upload-audio`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const audioPath = uploadResponse.data.file_path;

      // Clone voice
      const cloneResponse = await axios.post(`${API_URL}/voice/clone`, {
        user_id: user.id,
        audio_url: audioPath,
        voice_name: `${user.name}'s Voice`,
        description: 'Voice clone for Always Here Platform'
      });

      setVoiceId(cloneResponse.data.voice_id);
      
      // Save voice_id to user profile in database
      // TODO: Add API call to save voice_id to user profile
      
    } catch (error) {
      console.error('Voice cloning failed:', error);
      setError(error.response?.data?.detail || 'Failed to clone voice. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white shadow rounded-lg p-8">
        {!voiceId ? (
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Set Up Your Voice</h1>
            <p className="text-gray-600 mb-8">
              Record 60 seconds of your voice to create an AI clone. This is a one-time setup.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-blue-900 mb-3">What to talk about:</h3>
              <ul className="text-blue-800 space-y-2">
                <li>• Tell a story from your childhood</li>
                <li>• Share your favorite memory</li>
                <li>• Give advice to your children</li>
                <li>• Talk about your values and beliefs</li>
                <li>• Describe your family</li>
              </ul>
              <p className="text-sm text-blue-700 mt-4">
                💡 Speak naturally and with emotion. The AI learns your tone and personality.
              </p>
            </div>

            <div className="flex flex-col items-center space-y-6 mb-8">
              {!isRecording && !recordedBlob && (
                <button
                  onClick={startRecording}
                  className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-xl shadow-lg text-white bg-red-600 hover:bg-red-700 transition-all"
                >
                  <MicrophoneIcon className="h-8 w-8 mr-3" />
                  Start Recording (60 seconds)
                </button>
              )}

              {isRecording && (
                <div className="text-center">
                  <div className="text-6xl font-bold text-red-600 mb-4">
                    {timer}
                  </div>
                  <div className="inline-flex items-center px-6 py-3 bg-red-100 rounded-full mb-6">
                    <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse mr-3"></div>
                    <span className="text-red-600 font-medium">Recording... Keep talking!</span>
                  </div>
                  <button
                    onClick={stopRecording}
                    className="px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gray-800 hover:bg-gray-900"
                  >
                    <StopIcon className="h-5 w-5 inline mr-2" />
                    Stop Early
                  </button>
                </div>
              )}

              {recordedBlob && !voiceId && (
                <div className="w-full">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <CheckCircleIcon className="h-6 w-6 text-green-600 inline mr-2" />
                    <span className="text-green-800 font-medium">Recording complete!</span>
                  </div>
                  <audio
                    controls
                    src={URL.createObjectURL(recordedBlob)}
                    className="w-full mb-6"
                  />
                  <div className="flex space-x-4">
                    <button
                      onClick={uploadAndClone}
                      disabled={uploading}
                      className="flex-1 px-6 py-3 border border-transparent text-base font-semibold rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
                    >
                      {uploading ? 'Cloning Voice...' : 'Clone My Voice'}
                    </button>
                    <button
                      onClick={() => setRecordedBlob(null)}
                      disabled={uploading}
                      className="px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100"
                    >
                      Re-record
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        ) : (
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Voice Setup Complete!</h2>
            <p className="text-lg text-gray-600 mb-8">
              Your voice has been successfully cloned. You can now create text messages that will be converted to speech in your voice.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <p className="text-sm text-gray-600 mb-2">Your Voice ID:</p>
              <p className="text-sm font-mono text-gray-900 bg-white px-4 py-2 rounded border">{voiceId}</p>
              <p className="text-xs text-gray-500 mt-2">This ID is saved to your profile</p>
            </div>

            <div className="space-y-4">
              <a
                href="/recipients"
                className="inline-block px-8 py-4 border border-transparent text-lg font-semibold rounded-lg shadow-lg text-white bg-primary-600 hover:bg-primary-700"
              >
                Add Family Members
              </a>
              <p className="text-sm text-gray-600">
                Next: Add the people who will receive your messages
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
